import { Fragment, useState } from "react";
import { Transition } from "@headlessui/react";
import { InformationCircleIcon, XMarkIcon } from "@heroicons/react/20/solid";
import cn from "clsx";
import { NotificationEnums } from "@api/hooks/ambassdor/useNotify";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import { Button, Loader } from "@corecomponents/ui";
// Sample code for notification
// <Button onClick={() => {
//     showNotification({
//         description: "Description  " + new Date().getTime(),
//         type: NotificationEnums.ERROR,
//         title: "New Title",
//         onClose: () => {
//             console.log('closed');
//         },
//         content: <div className={'bg-pink-600'}>
//             Error
//         </div>
//     })
// }}>
//     Hllo
// </Button>
export default function SuccessNotification({
  index,
  type,
  totalCount,
  content,
  title,
  description,
  onClose,
  variant,
  alignment,
}: {
  index: number;
  totalCount: number;
  title?: string;
  content?: any;
  description?: string;
  type: NotificationEnums;
  onClose: () => void;
  variant?: "white" | "black";
  alignment?: "left" | "right" | "center";
}) {
  const [show, setShow] = useState(true);

  return (
    <>
      {/* Global notification live region, render this permanently at the end of the document */}
      <div
        aria-live="assertive"
        className={cn(
          " pointer-events-none fixed inset-0 flex items-end px-4 py-6 z-[99999] sm:items-start sm:p-6"
        )}
      >
        <div
          className={cn("flex w-full flex-col items-center space-y-4", {
            "sm:items-end": alignment === "right",
            "sm:items-start": alignment === "left",
          })}
        >
          {/* Notification panel, dynamically insert this into the live region when it needs to be displayed */}
          <Transition
            show={show}
            as={Fragment}
            enter="transform ease-out duration-300 transition"
            enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
            enterTo="translate-y-0 opacity-100 sm:translate-x-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className={cn(
                "pointer-events-auto  max-w-[26rem] transition-all duration-300 animate-notificationZoom animated overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5",
                {
                  ["  "]: index === totalCount - 1,
                }
              )}
              style={{
                transform: `translateY(${
                  (totalCount - index - 1) * 5
                }px) scale(${(100 - (totalCount - index - 1) * 4) / 100})`,
              }}
            >
              <div
                className={cn("p-4", {
                  "bg-gray-700": variant === "black",
                })}
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    {type === NotificationEnums.LOADING && <Loader scale={1} />}
                    {type === NotificationEnums.SUCCESS && (
                      <CheckCircleIcon
                        className="h-6 w-6 text-green-400"
                        aria-hidden="true"
                      />
                    )}
                    {type === NotificationEnums.ERROR && (
                      <ExclamationCircleIcon
                        className="h-6 w-6 text-red-400"
                        aria-hidden="true"
                      />
                    )}
                    {type === NotificationEnums.WARNING && (
                      <ExclamationCircleIcon
                        className="h-6 w-6 text-yellow-600"
                        aria-hidden="true"
                      />
                    )}
                    {type === NotificationEnums.INFO && (
                      <InformationCircleIcon
                        className="h-6 w-6 text-blue-400"
                        aria-hidden="true"
                      />
                    )}
                  </div>
                  <div className="ml-3 pt-0.5">
                    {title && (
                      <p
                        className={cn("text-sm font-medium", {
                          ["text-black-900"]: variant === "white",
                          ["text-white"]: variant === "black",
                        })}
                      >
                        {title}
                      </p>
                    )}
                    {description && (
                      <p className="mt-1 text-sm text-gray-500">
                        {description}
                      </p>
                    )}
                    {content || <></>}
                  </div>

                  <div className="ml-4 flex flex-shrink-0">
                    <Button variant={"naked"} onClick={onClose}>
                      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </>
  );
}
